// src/components/Auth/Login.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import styles from './Login.module.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

interface LoginProps {
  embedded?: boolean;
}

const Login: React.FC<LoginProps> = ({ embedded = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { setMercenaryId, setMercenary } = useMercenary();
  const { setUser } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('banned') === 'true') {
      setError('Your account has been banned. If you believe this is a mistake, please contact support.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE}/api/auth/login`, { email, password }, { withCredentials: true });
      const user = response.data;
      setUser(user);

      try {
        const mercenaryResponse = await axios.get(`${API_BASE}/api/mercenary/first`, { withCredentials: true });
        const mercenary = mercenaryResponse.data;

        if (mercenary) {
          setMercenaryId(mercenary.id);
          setMercenary(mercenary);
          if (socket) {
            socket.emit('join_room', user.id);
          }
          navigate('/home');
        } else {
          navigate('/create-mercenary');
        }
      } catch (mercErr: any) {
        // 404 means no mercenary exists yet (fresh account), send to create page
        if (axios.isAxiosError(mercErr) && mercErr.response?.status === 404) {
          navigate('/create-mercenary');
        } else {
          // Non-404 error fetching mercenary, still logged in, go home
          console.error('Error fetching mercenary after login:', mercErr);
          navigate('/home');
        }
      }
    } catch (err: any) {
      if (err?.response?.data?.banned) {
        setError(err.response.data.message);
      } else {
        setError('Invalid email or password');
      }
      console.error(err);
    }
  };

  const handleDiscordLogin = () => {
    window.location.href = `${API_BASE}/api/auth/discord`;
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  const formContent = (
    <>
      <form onSubmit={handleLogin} className={styles.form}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Email</label>
          <input
            className={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Password</label>
          <input
            className={styles.input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <button type="submit" className={styles.btnLogin}>
          Enter
        </button>
      </form>

      <div className={styles.divider}>
        <span className={styles.dividerText}>or</span>
      </div>

      <button onClick={handleDiscordLogin} className={styles.btnDiscord}>
        <svg className={styles.discordIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
        Continue with Discord
      </button>

      <button onClick={handleGoogleLogin} className={styles.btnGoogle}>
        <svg className={styles.googleIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>
    </>
  );

  if (embedded) {
    return <div className={styles.embeddedForm}>{formContent}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Njordheim</h1>
        <p className={styles.subtitle}>Enter the Realm</p>
        <hr className={styles.dividerTop} />

        {formContent}

        <p className={styles.footer}>
          No account?{' '}
          <Link to="/register" className={styles.link}>Create one here</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
